import { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import IntentService, { errorMessage } from 'src/services/IntentService';
import { rubles, safeExternalUrl, validBudget } from 'src/utils/intentEconomics';
import { clearIntentHandoff, readIntentHandoff } from 'src/utils/intentHandoff';

import { requestKey } from './PackageCheckout';

import type { Capabilities, Order, Renewal, Workspace } from 'src/services/IntentService';

const states: Record<string, string> = {
    created: 'Заказ создан',
    submitting: 'Проверяем создание платежа',
    pending: 'Ожидаем оплату',
    paid: 'Оплачено',
    cancelled: 'Отменено',
    refund_requested: 'Запрошен возврат',
};
export function OrderCard({ order, onChange }: { order: Order; onChange: (o: Order) => void }) {
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const act = async (refund = false) => {
        setBusy(true);
        setError('');
        try {
            onChange(refund ? await IntentService.refund(order.id) : await IntentService.order(order.id));
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    const url = safeExternalUrl(order.confirmation_url);
    return (
        <Box className="intent-order-row">
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
            >
                <div>
                    <Typography fontWeight={600}>
                        {order.kind === 'gift' ? 'Подарок на запуск' : (order.snapshot.leads || '') + ' лидов'}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        {new Date(order.created_at).toLocaleDateString('ru-RU')} ·{' '}
                        {states[order.state] || 'Проверяется'}
                    </Typography>
                </div>
                <Typography fontWeight={600}>{rubles(order.amount)} с карты</Typography>
            </Stack>
            {Number(order.wallet_used) > 0 && (
                <Typography variant="body2">Со счёта: {rubles(order.wallet_used)}</Typography>
            )}
            {order.snapshot.auto_not_enabled && (
                <Alert
                    severity="info"
                    sx={{ mt: 1 }}
                >
                    Оплата прошла, но метод не сохранился. Автопополнение не включено.
                </Alert>
            )}
            {order.detail && (
                <Typography
                    variant="body2"
                    mt={1}
                >
                    {order.detail}
                </Typography>
            )}
            {order.gift_url && (
                <Stack
                    direction="row"
                    mt={2}
                    spacing={1}
                    alignItems="center"
                >
                    <TextField
                        size="small"
                        fullWidth
                        label="Ссылка на подарок"
                        value={order.gift_url}
                        inputProps={{ readOnly: true }}
                    />
                    <Typography variant="body2">{order.gift_activated_at ? 'Получен' : 'Ждёт получателя'}</Typography>
                </Stack>
            )}
            <Stack
                direction="row"
                gap={1}
                mt={1}
                flexWrap="wrap"
            >
                {!['paid', 'cancelled', 'refund_requested'].includes(order.state) && (
                    <Button
                        disabled={busy}
                        onClick={() => act()}
                    >
                        Проверить оплату
                    </Button>
                )}
                {order.state === 'pending' && url?.startsWith('https://') && (
                    <Button
                        component="a"
                        href={url}
                    >
                        Продолжить оплату
                    </Button>
                )}
                {order.state === 'paid' && (
                    <Button
                        size="small"
                        disabled={busy}
                        onClick={() => act(true)}
                    >
                        Запросить возврат
                    </Button>
                )}
            </Stack>
            {error && (
                <Alert
                    severity="error"
                    sx={{ mt: 1 }}
                >
                    {error}
                </Alert>
            )}
        </Box>
    );
}

export function BillingPanel({
    workspace,
    wallet,
    onGift,
    onReferral,
}: {
    workspace: Workspace | null;
    wallet: string | null;
    onGift: () => void;
    onReferral: () => void;
}) {
    const [orders, setOrders] = useState<Order[] | null>(null);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [rule, setRule] = useState<Renewal | null>(null);
    const [error, setError] = useState('');
    useEffect(() => {
        let live = true;
        setOrders(null);
        setOrdersLoading(true);
        setError('');
        setRule(null);
        IntentService.orders()
            .then((data) => {
                if (live) setOrders(data);
            })
            .catch((e) => {
                if (live) setError(errorMessage(e));
            })
            .finally(() => {
                if (live) setOrdersLoading(false);
            });
        if (workspace)
            IntentService.renewal(workspace.id)
                .then((r) => {
                    if (live) setRule(r);
                })
                .catch((e) => {
                    if (live) setError(errorMessage(e));
                });
        return () => {
            live = false;
        };
    }, [workspace?.id]);
    return (
        <Stack spacing={3}>
            <div>
                <Typography
                    variant="h5"
                    component="h2"
                >
                    Пакеты и оплата
                </Typography>
                <Typography
                    color="text.secondary"
                    mt={1}
                >
                    {wallet === null ? 'Баланс недоступен.' : `На счёте: ${rubles(wallet)}.`} Покупка и фактический
                    расход учитываются отдельно.
                </Typography>
            </div>
            {error && <Alert severity="error">{error}</Alert>}
            {workspace?.streams.map((stream) => (
                <Box
                    key={stream.id}
                    className="intent-plain-block"
                >
                    <Typography fontWeight={600}>{stream.name || stream.category_name}</Typography>
                    <Typography>Осталось {stream.balance} лидов</Typography>
                    {stream.access_expires_at && (
                        <>
                            <Typography>
                                Доступ оплачен до {new Date(stream.access_expires_at).toLocaleDateString('ru-RU')}
                            </Typography>
                            <Button
                                component="a"
                                href={'/leadstream/' + stream.id}
                            >
                                Продлить доступ
                            </Button>
                        </>
                    )}
                </Box>
            ))}
            {rule && (
                <Box className="intent-plain-block">
                    <Typography fontWeight={600}>Автопополнение {rule.enabled ? 'включено' : 'выключено'}</Typography>
                    {rule.enabled && (
                        <Typography variant="body2">
                            При остатке {rule.threshold} лидов. Лимит: {rubles(rule.monthly_limit)} в месяц.
                        </Typography>
                    )}
                    {rule.last_error && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {rule.last_error}
                        </Typography>
                    )}
                    {!!rule.pending_payments && (
                        <Typography variant="body2">
                            В обработке: {rule.pending_payments} платежей. Отключение не отменяет уже созданный платёж.
                        </Typography>
                    )}
                    {rule.enabled && (
                        <Button
                            onClick={async () => {
                                if (!workspace) return;
                                try {
                                    setRule(await IntentService.disableRenewal(workspace.id));
                                } catch (e) {
                                    setError(errorMessage(e));
                                }
                            }}
                        >
                            Отключить
                        </Button>
                    )}
                </Box>
            )}
            <Stack
                direction="row"
                gap={1}
                flexWrap="wrap"
            >
                <Button
                    variant="outlined"
                    onClick={onGift}
                >
                    Подарить запуск
                </Button>
                <Button onClick={onReferral}>Пригласить знакомого</Button>
            </Stack>
            <Typography variant="h6">История покупок аккаунта</Typography>
            {ordersLoading && <Typography color="text.secondary">Загружаем покупки…</Typography>}
            {!ordersLoading && orders === null && (
                <Typography color="text.secondary">История покупок недоступна.</Typography>
            )}
            {orders?.length === 0 && (
                <Typography color="text.secondary">
                    Новых покупок пока нет. Прежние покупки сохранены в текущих потоках.
                </Typography>
            )}
            {orders?.map((order) => (
                <OrderCard
                    key={order.id}
                    order={order}
                    onChange={(next) => setOrders((old) => old?.map((o) => (o.id === next.id ? next : o)) || null)}
                />
            ))}
        </Stack>
    );
}

export function ReferralPanel() {
    const [data, setData] = useState<Awaited<ReturnType<typeof IntentService.referral>> | null>(null);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        let live = true;
        IntentService.referral()
            .then((d) => {
                if (live) setData(d);
            })
            .catch((e) => {
                if (live) setError(errorMessage(e));
            });
        return () => {
            live = false;
        };
    }, []);
    return (
        <Stack spacing={3}>
            <Typography
                variant="h5"
                component="h2"
            >
                Пригласить знакомого
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {data && (
                <>
                    <Typography>
                        {Number(data.reward) > 0
                            ? 'Каждому по ' +
                              rubles(data.reward) +
                              ' на услуги после первого платного запуска приглашённого от 1 500 ₽ с карты.'
                            : 'Поделитесь Лидсманией с тем, кому нужны новые клиенты. Бонусная акция сейчас не действует.'}
                    </Typography>
                    <TextField
                        label="Ваша ссылка"
                        value={data.url}
                        inputProps={{ readOnly: true }}
                    />
                    <Button
                        variant="contained"
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={async () => {
                            try {
                                await navigator.clipboard.writeText(data.url);
                                setCopied(true);
                            } catch {
                                setError('Не удалось скопировать автоматически. Выделите ссылку и скопируйте её.');
                            }
                        }}
                    >
                        {copied ? 'Скопировано' : 'Скопировать ссылку'}
                    </Button>
                    <Typography color="text.secondary">
                        Приглашено: {data.invited} · Бонус начислен: {data.rewarded}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Один новый аккаунт — одно приглашение. Самоприглашение не допускается. Начисление проверяется
                        при возврате заказа.
                    </Typography>
                    {data.history.map((r) => (
                        <Box
                            key={r.id}
                            className="intent-order-row"
                        >
                            Приглашение {r.id} ·{' '}
                            {r.state === 'rewarded' ? 'Начислено ' + rubles(r.reward) : 'Ожидает первого запуска'}
                        </Box>
                    ))}
                </>
            )}
        </Stack>
    );
}

export function GiftPanel({
    capabilities,
    onOrder,
    onActivated,
}: {
    capabilities: Capabilities | null;
    onOrder: (o: Order) => void;
    onActivated: () => void;
}) {
    const [amount, setAmount] = useState('1500');
    const [message, setMessage] = useState('');
    const [key, setKey] = useState(requestKey);
    const [busy, setBusy] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState('');
    const [token, setToken] = useState(() => readIntentHandoff('gift'));
    const [preview, setPreview] = useState<{ amount: string; message: string } | null>(null);
    useEffect(() => {
        let live = true;
        if (token)
            IntentService.previewGift(token)
                .then((p) => {
                    if (live) setPreview(p);
                })
                .catch((e) => {
                    if (live) setError(errorMessage(e));
                });
        return () => {
            live = false;
        };
    }, [token]);
    const buy = async () => {
        setBusy(true);
        setError('');
        try {
            const order = await IntentService.checkout({
                request_key: key,
                gift_amount: amount,
                gift_message: message,
                expected_card_amount: Number(amount).toFixed(2),
            });
            onOrder(order);
            const url = safeExternalUrl(order.confirmation_url);
            if (order.state === 'pending' && url?.startsWith('https://')) window.location.assign(url);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Stack spacing={3}>
            <Typography
                variant="h5"
                component="h2"
            >
                {token ? 'Вам подарили запуск' : 'Подарить запуск'}
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {token ? (
                <>
                    {preview && (
                        <>
                            <Typography variant="h4">{rubles(preview.amount)}</Typography>
                            <Typography>{preview.message}</Typography>
                            <Typography color="text.secondary">
                                Бюджет поступит на ваш счёт. Количество лидов зависит от выбранной ниши. Карта дарителя
                                не привязывается.
                            </Typography>
                        </>
                    )}
                    <Button
                        variant="contained"
                        disabled={busy || !preview}
                        onClick={async () => {
                            setBusy(true);
                            try {
                                await IntentService.claimGift(token);
                                clearIntentHandoff('gift');
                                setToken(null);
                                onActivated();
                            } catch (e) {
                                setError(errorMessage(e));
                            } finally {
                                setBusy(false);
                            }
                        }}
                    >
                        Принять подарок
                    </Button>
                </>
            ) : (
                <>
                    <Typography color="text.secondary">
                        Подарите бюджет на поиск клиентов. Получатель выберет нишу и пакет; деньги не привязаны к цене
                        одного направления.
                    </Typography>
                    <TextField
                        label="Сумма подарка, ₽"
                        type="number"
                        value={amount}
                        inputProps={{ min: 1500, max: 1000000, step: 0.01 }}
                        onChange={(e) => {
                            setAmount(e.target.value);
                            setKey(requestKey());
                        }}
                        error={!validBudget(amount)}
                        helperText="От 1 500 ₽"
                    />
                    <TextField
                        label="Сообщение получателю"
                        multiline
                        rows={3}
                        value={message}
                        inputProps={{ maxLength: 500 }}
                        onChange={(e) => {
                            setMessage(e.target.value);
                            setKey(requestKey());
                        }}
                    />
                    <Typography variant="body2">
                        После оплаты получите ссылку и сами передадите её знакомому. Получатель активирует подарок один
                        раз. Автопополнение и платёжный метод не передаются.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                            />
                        }
                        label="Проверил сумму и условия подарка"
                    />
                    {!capabilities?.gifts && (
                        <Alert severity="info">
                            Оплата подарков станет доступна после подключения магазина и утверждения условий.
                        </Alert>
                    )}
                    <Button
                        variant="contained"
                        disabled={busy || !validBudget(amount) || !agreed || !capabilities?.gifts}
                        onClick={buy}
                    >
                        {busy ? 'Проверяем…' : 'Подарить ' + rubles(amount)}
                    </Button>
                </>
            )}
        </Stack>
    );
}
